// src/pages/compony/FileUploader.jsx
import React, { useRef, useState } from 'react';
import api from '../../core/api';
import CropModal from './CropModal';

const API_BASE = api.defaults.baseURL || '';
const MAX_PICK = 10 * 1024 * 1024; // قبل از کراپ؛ خروجی کراپ کوچک است

export default function FileUploader({
  label,
  field,
  icon = 'fa-image',
  currentUrl,
  aspect = 1,
  outW = 512,
  outH = 512,
  hint,
  onChange, // (url|'') => void
  onMessage, // (text, type) => void
}) {
  const inputRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);
  const [cropSrc, setCropSrc] = useState(null);
  const [progress, setProgress] = useState(null); // null | 0..100
  const [removing, setRemoving] = useState(false);

  const busy = progress !== null || removing;

  const previewUrl = currentUrl
    ? /^(https?:|blob:)/.test(currentUrl)
      ? currentUrl
      : `${API_BASE}${currentUrl}`
    : null;

  /* ---------- انتخاب فایل ---------- */
  const pickFile = (file) => {
    if (!file || busy) return;
    if (!/^image\/(png|jpe?g|webp)$/.test(file.type)) {
      onMessage?.('فقط فرمت PNG، JPG یا WebP مجاز است', 'danger');
      return;
    }
    if (file.size > MAX_PICK) {
      onMessage?.('حجم فایل نباید بیشتر از ۱۰ مگابایت باشد', 'danger');
      return;
    }
    setCropSrc(URL.createObjectURL(file));
  };

  const closeCrop = () => {
    if (cropSrc) URL.revokeObjectURL(cropSrc);
    setCropSrc(null);
    if (inputRef.current) inputRef.current.value = '';
  };

  /* ---------- آپلود فوری بعد از کراپ ---------- */
  const upload = async (blob) => {
    closeCrop();
    setProgress(0);
    try {
      const fd = new FormData();
      fd.append('file', blob, `${field}.png`);
      const res = await api.post(`/company/file/${field}`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 60000,
        onUploadProgress: (e) => {
          if (e.total) setProgress(Math.round((e.loaded * 100) / e.total));
        },
      });
      onChange?.(res.data.url);
      onMessage?.(`${label} با موفقیت آپلود شد`, 'success');
    } catch (err) {
      onMessage?.(err.response?.data?.error || 'خطا در آپلود تصویر', 'danger');
    } finally {
      setProgress(null);
    }
  };

  /* ---------- حذف فوری ---------- */
  const remove = async () => {
    if (!window.confirm(`${label} حذف شود؟`)) return;
    setRemoving(true);
    try {
      await api.delete(`/company/file/${field}`);
      onChange?.('');
      onMessage?.(`${label} حذف شد`, 'success');
    } catch (err) {
      onMessage?.(err.response?.data?.error || 'خطا در حذف تصویر', 'danger');
    } finally {
      setRemoving(false);
    }
  };

  /* ---------- درگ و دراپ ---------- */
  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    pickFile(e.dataTransfer.files?.[0]);
  };

  const processing = progress === 100;

  return (
    <div className="up-card">
      <div className="up-title">{label}</div>

      <div
        className={`up-drop ${dragOver ? 'drag' : ''} ${previewUrl ? 'has-file' : ''}`}
        style={{ aspectRatio: `${aspect}` }}
        onClick={() => !busy && inputRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); if (!busy) setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
      >
        {previewUrl ? (
          <img src={previewUrl} alt={label} className="up-img" />
        ) : (
          <div className="up-empty">
            <div className="up-icon"><i className={`fa-solid ${icon}`} /></div>
            <b>{dragOver ? 'رها کنید...' : 'فایل را بکشید و رها کنید'}</b>
            <span>یا کلیک کنید</span>
          </div>
        )}

        {previewUrl && !busy && (
          <div className="up-hover">
            <i className="fa-solid fa-cloud-arrow-up" /> تغییر تصویر
          </div>
        )}

        {busy && (
          <div className="up-busy">
            {removing ? (
              <span className="up-busy-text">در حال حذف...</span>
            ) : (
              <>
                <div className="up-ring" style={{ '--p': processing ? 100 : progress }}>
                  <span>{processing ? <i className="fa-solid fa-spinner fa-spin" /> : `${progress}%`}</span>
                </div>
                <span className="up-busy-text">
                  {processing ? 'در حال پردازش...' : 'در حال آپلود...'}
                </span>
              </>
            )}
          </div>
        )}

        <input
          ref={inputRef}
          type="file"
          accept="image/png,image/jpeg,image/webp"
          hidden
          onChange={(e) => pickFile(e.target.files?.[0])}
        />
      </div>

      <div className="up-foot">
        <span className="up-hint">{hint}</span>
        {previewUrl && (
          <button type="button" className="up-remove" onClick={remove} disabled={busy}>
            <i className="fa-solid fa-trash" /> حذف
          </button>
        )}
      </div>

      {cropSrc && (
        <CropModal
          src={cropSrc}
          aspect={aspect}
          outW={outW}
          outH={outH}
          title={`برش ${label}`}
          onCancel={closeCrop}
          onConfirm={upload}
        />
      )}
    </div>
  );
}